import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Home, Loader2, PartyPopper, Search, Sparkles } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import {
  enTarifa,
  enUnidad,
  type Cargo,
  type CotizacionDeCargo,
  type Predio,
  type TipoDeCargo,
} from './contrato';

interface Identidad {
  nombre: string;
  direccion?: string | null;
  telefono?: string | null;
  correo?: string | null;
}

export function ModalDeCargo({
  tipos,
  alCerrar,
  alRegistrar,
}: {
  tipos: TipoDeCargo[];
  alCerrar: () => void;
  alRegistrar: (c: Cargo) => void;
}) {
  const { puede } = useSesion();
  const activos = tipos.filter((t) => t.activo);
  const [codigo, setCodigo] = useState(activos[0]?.codigo ?? '');
  const tipo = activos.find((t) => t.codigo === codigo);
  const esAseo = tipo?.sujeto === 'PREDIO';

  const [buscarPredio, setBuscarPredio] = useState('');
  const [predio, setPredio] = useState<Predio | null>(null);
  const [identificacion, setIdentificacion] = useState('');
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [fecha, setFecha] = useState(hoy());
  const [cantidad, setCantidad] = useState('');
  const [informe, setInforme] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [observacion, setObservacion] = useState('');

  const medido = Number(cantidad.replace(',', '.'));
  const cantidadValida = Number.isFinite(medido) && medido > 0;
  const cantidadDiferida = useValorDiferido(cantidadValida ? medido : 0);
  const textoDiferido = useValorDiferido(buscarPredio.trim());

  const predios = useQuery({
    queryKey: ['cargos', 'predios', textoDiferido],
    enabled: esAseo && !predio && textoDiferido.length >= 3,
    queryFn: () => api.get<Predio[]>('/cargos/predios', { query: { texto: textoDiferido } }),
  });

  const cotizacion = useQuery({
    queryKey: ['cargos', 'cotizacion', codigo, cantidadDiferida],
    enabled: !!tipo && cantidadDiferida > 0,
    queryFn: () =>
      api.get<CotizacionDeCargo>('/cargos/cotizacion', {
        query: { tipo: codigo, cantidad: cantidadDiferida },
      }),
  });

  const registroCivil = useMutation({
    mutationFn: () => api.get<Identidad>(`/identidades/${identificacion.trim()}`),
    onSuccess: (i) => {
      setNombre(i.nombre);
      if (i.direccion) setDireccion(i.direccion);
      if (i.telefono) setTelefono(i.telefono);
      if (i.correo) setEmail(i.correo);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO CONSULTAR'),
  });

  const registrar = useMutation({
    mutationFn: () =>
      api.post<Cargo>('/cargos', {
        tipoCodigo: codigo,
        cuentaId: esAseo ? predio?.cuentaId : null,
        organizador: esAseo
          ? null
          : { identificacion: identificacion.trim(), nombre, direccion, telefono, email },
        fecha,
        cantidad: medido,
        informe,
        descripcion,
        observacion,
      }),
    onSuccess: (c) => {
      notificar.exito(
        `${c.nombre} · $${c.total.toFixed(2)}`,
        c.numeroFactura || c.facturaId ? 'Cargo registrado y facturado' : 'Cargo registrado',
      );
      alRegistrar(c);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO REGISTRAR'),
  });

  const quienListo = esAseo
    ? predio !== null
    : /^\d{10}(\d{3})?$/.test(identificacion.trim()) && nombre.trim().length > 0;
  const listo =
    !!tipo &&
    quienListo &&
    cantidadValida &&
    fecha.length === 10 &&
    informe.trim().length > 0 &&
    descripcion.trim().length > 0 &&
    !registrar.isPending;

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Nuevo cargo puntual"
      subtitulo="Aseo de espacios particulares o evento público · Art. 9 literales e y f"
      icono={Sparkles}
      ancho="ancho"
      cerrarAlPulsarFuera={false}
    >
      <div className="grid gap-4">
        <div role="radiogroup" aria-label="Tipo de cargo" className="grid gap-2 sm:grid-cols-2">
          {activos.map((t) => {
            const elegido = t.codigo === codigo;
            const Icono = t.sujeto === 'PREDIO' ? Home : PartyPopper;
            return (
              <button
                key={t.codigo}
                type="button"
                role="radio"
                aria-checked={elegido}
                onClick={() => {
                  setCodigo(t.codigo);
                  if (t.sujeto !== tipo?.sujeto) setCantidad('');
                }}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                  elegido
                    ? 'border-marca-400 bg-marca-50 ring-1 ring-marca-400'
                    : 'border-armazon-200 bg-white hover:border-armazon-300'
                }`}
              >
                <Icono
                  className={`mt-0.5 size-4 shrink-0 ${elegido ? 'text-marca-700' : 'text-armazon-400'}`}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block text-[11px] font-bold uppercase leading-snug text-armazon-800">
                    {t.literal.toUpperCase()}) {t.nombre}
                  </span>
                  <span className="importe block text-xs font-semibold text-marca-700">
                    ${enTarifa(t.tarifaUnitaria)}/{enUnidad(t.unidad)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {esAseo ? (
          <fieldset className="grid gap-3 rounded-xl border border-armazon-200 p-4">
            <legend className="px-1 text-[10px] font-semibold uppercase text-armazon-500">
              El predio limpiado · paga su titular
            </legend>
            {predio ? (
              <div className="flex items-start justify-between gap-3 rounded-lg bg-armazon-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold uppercase text-armazon-800">{predio.titular}</p>
                  <p className="importe text-xs text-armazon-500">
                    {predio.identificacion} · CUENTA {predio.numeroCuenta}
                  </p>
                  <p className="truncate text-[11px] uppercase text-armazon-500">
                    {predio.direccion ?? 'SIN DIRECCIÓN'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPredio(null)}
                  className="shrink-0 text-[11px] font-bold uppercase text-marca-700 hover:underline"
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <>
                <Campo etiqueta="Buscar el predio">
                  <span className="relative block">
                    <Search
                      className="pointer-events-none absolute left-3 top-3 size-4 text-armazon-400"
                      aria-hidden
                    />
                    <input
                      value={buscarPredio}
                      onChange={(e) => setBuscarPredio(e.target.value)}
                      placeholder="Número de cuenta, medidor, dirección, cédula o nombre del titular"
                      autoFocus
                      className={`${ENTRADA} pl-9`}
                    />
                  </span>
                </Campo>
                {textoDiferido.length >= 3 && (
                  <ul className="max-h-56 divide-y divide-armazon-100 overflow-auto rounded-lg border border-armazon-200">
                    {predios.isPending && (
                      <li className="px-3 py-3 text-xs uppercase text-armazon-400">Buscando…</li>
                    )}
                    {predios.data?.length === 0 && (
                      <li className="px-3 py-3 text-xs uppercase text-armazon-400">
                        Ningún predio coincide
                      </li>
                    )}
                    {predios.data?.map((p) => (
                      <li key={p.cuentaId}>
                        <button
                          type="button"
                          onClick={() => setPredio(p)}
                          className="block w-full px-3 py-2 text-left hover:bg-marca-50"
                        >
                          <span className="block text-xs font-bold uppercase text-armazon-800">
                            {p.titular}
                            {!p.activo && (
                              <span className="ml-2 text-[10px] text-amber-700">INACTIVO</span>
                            )}
                          </span>
                          <span className="importe block text-[11px] text-armazon-500">
                            CUENTA {p.numeroCuenta} · {p.identificacion}
                          </span>
                          <span className="block truncate text-[10px] uppercase text-armazon-400">
                            {p.direccion}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </fieldset>
        ) : (
          <fieldset className="grid gap-3 rounded-xl border border-armazon-200 p-4">
            <legend className="px-1 text-[10px] font-semibold uppercase text-armazon-500">
              Quién organizó el evento · paga él
            </legend>
            <div className="grid gap-3 sm:grid-cols-[14rem_1fr]">
              <Campo etiqueta="RUC o cédula">
                <span className="flex gap-2">
                  <input
                    value={identificacion}
                    onChange={(e) => setIdentificacion(e.target.value.replace(/\D/g, ''))}
                    inputMode="numeric"
                    maxLength={13}
                    className={`${ENTRADA} importe`}
                  />
                  {puede('identidades.consultar') && (
                    <button
                      type="button"
                      onClick={() => registroCivil.mutate()}
                      disabled={!/^\d{10}(\d{3})?$/.test(identificacion) || registroCivil.isPending}
                      title="Traer los datos del Registro Civil o del SRI"
                      className="inline-flex h-10 shrink-0 items-center rounded-lg border border-armazon-200 px-3 text-armazon-600 transition-colors hover:bg-armazon-50 disabled:opacity-40"
                    >
                      {registroCivil.isPending ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        <Search className="size-4" aria-hidden />
                      )}
                    </button>
                  )}
                </span>
              </Campo>
              <Campo etiqueta="Nombre o razón social">
                <input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value.toUpperCase())}
                  className={ENTRADA}
                />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Campo etiqueta="Dirección">
                <input
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
              <Campo etiqueta="Teléfono">
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
              <Campo etiqueta="Correo">
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
            </div>
            <p className="text-[10px] uppercase text-armazon-400">
              Si ya es cliente se usa el que está; si no, se registra solo para esta factura, sin
              entrar en la lista del catastro.
            </p>
          </fieldset>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Campo etiqueta={esAseo ? 'Fecha de la limpieza' : 'Fecha del evento'}>
            <input
              type="date"
              value={fecha}
              max={hoy()}
              onChange={(e) => setFecha(e.target.value)}
              className={`${ENTRADA} importe`}
            />
          </Campo>
          <Campo
            etiqueta={
              tipo
                ? `${tipo.unidad === 't' ? 'Peso' : 'Cantidad'} (${enUnidad(tipo.medida, true)})`
                : 'Cantidad'
            }
          >
            <input
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              inputMode="decimal"
              className={`${ENTRADA} importe text-right`}
            />
          </Campo>
          <Campo etiqueta="Nº de informe técnico">
            <input
              value={informe}
              onChange={(e) => setInforme(e.target.value.toUpperCase())}
              className={`${ENTRADA} importe`}
            />
          </Campo>
        </div>

        <Campo etiqueta={esAseo ? 'Qué se limpió' : 'Evento y lugar'}>
          <input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value.toUpperCase())}
            maxLength={300}
            placeholder={
              esAseo
                ? 'FRENTE Y VEREDA · LOTE SIN CERRAMIENTO'
                : 'CLAUSURA DEL CAMPEONATO · ESTADIO'
            }
            className={ENTRADA}
          />
        </Campo>

        <Campo etiqueta="Observación">
          <input
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            className={ENTRADA}
          />
        </Campo>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-armazon-100 pt-4">
          <div className="text-[11px] uppercase text-armazon-500">
            {cotizacion.data && tipo ? (
              <>
                <span className="importe">
                  {`${cotizacion.data.cantidad.toLocaleString('es-EC', { maximumFractionDigits: 6 })} ${enUnidad(tipo.unidad)} × $${enTarifa(cotizacion.data.tarifaUnitaria)}`}
                </span>
                <span className="importe block text-lg font-bold text-armazon-900">
                  ${cotizacion.data.total.toFixed(2)}
                </span>
                <span className="block text-[10px] text-armazon-400">
                  {esAseo
                    ? 'Se factura con las demás limpiezas del mes, al terminar el mes'
                    : 'Sale ya con su factura, pendiente de cobro'}
                </span>
              </>
            ) : (
              'Escriba la cantidad para ver el importe'
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={alCerrar}
              className="h-10 rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-600 hover:bg-armazon-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => registrar.mutate()}
              disabled={!listo}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-5 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:opacity-50"
            >
              {registrar.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
              {esAseo ? 'Registrar' : 'Registrar y facturar'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-semibold uppercase text-armazon-600">{etiqueta}</span>
      {children}
    </label>
  );
}

function hoy(): string {
  const d = new Date();
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

const ENTRADA =
  'h-10 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors focus:border-marca-400';
